/**
 * Inline rename for a foundation baseline (persuasion run name).
 * Uses PATCH /persuasion-lab/runs/:runId { name }.
 */

import type { PersuasionRun } from '@/services/persuasion-lab'
import { persuasionLabService } from '@/services/persuasion-lab'
import { Pencil } from 'lucide-react'
import { FC, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'

/* eslint-disable react-refresh/only-export-components -- utility + component module */

export function foundationBaselineDisplayName(run: Pick<PersuasionRun, '_id' | 'name'>): string {
    return (run.name || '').trim() || `Baseline ${(run._id ?? '').slice(0, 8)}`
}

interface EditableFoundationBaselineNameProps {
    run: PersuasionRun
    /** Called after a successful save with the persisted name */
    onRenamed?: (runId: string, name: string) => void
    /** Table cell: title + pencil on hover. Header: larger title beside pencil */
    variant?: 'table' | 'header'
    onOpen?: (runId: string) => void
    showRecentHint?: boolean
    recentStepLabel?: string
}

export const EditableFoundationBaselineName: FC<EditableFoundationBaselineNameProps> = ({
    run,
    onRenamed,
    variant = 'table',
    onOpen,
    showRecentHint,
    recentStepLabel,
}) => {
    const [editing, setEditing] = useState(false)
    const [draft, setDraft] = useState('')
    const [saving, setSaving] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const displayName = foundationBaselineDisplayName(run)

    useEffect(() => {
        if (editing) inputRef.current?.focus()
    }, [editing])

    const startEdit = (e?: React.MouseEvent) => {
        e?.preventDefault()
        e?.stopPropagation()
        setDraft(displayName)
        setEditing(true)
    }

    const cancelEdit = () => {
        setEditing(false)
        setDraft('')
    }

    const saveEdit = async () => {
        if (saving) return
        const nextName = draft.trim() || 'Brand baseline'
        setEditing(false)
        if (nextName === (run.name || '').trim()) return
        setSaving(true)
        try {
            await persuasionLabService.updateRun(run._id, { name: nextName })
            onRenamed?.(run._id, nextName)
            toast.success('Baseline renamed', { duration: 3000 })
        } catch {
            toast.error('Failed to rename baseline', { duration: 4000 })
        } finally {
            setSaving(false)
        }
    }

    if (variant === 'header') {
        return (
            <div className='flex min-w-0 items-center gap-2'>
                {editing ? (
                    <input
                        ref={inputRef}
                        type='text'
                        value={draft}
                        disabled={saving}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault()
                                void saveEdit()
                            }
                            if (e.key === 'Escape') cancelEdit()
                        }}
                        onBlur={() => void saveEdit()}
                        className='min-w-0 flex-1 rounded-md border border-violet-200 px-2 py-1 text-xl font-bold text-violet-base focus:outline-none focus:ring-2 focus:ring-cz-focus/30'
                        aria-label='Baseline name'
                    />
                ) : (
                    <>
                        <span className='text-base font-bold text-violet-base break-words leading-tight sm:text-xl'>
                            {displayName}
                        </span>
                        <button
                            type='button'
                            onClick={startEdit}
                            className='rounded p-1.5 text-slate-400 hover:bg-violet-50 hover:text-violet-700'
                            title='Rename baseline'
                            aria-label='Rename baseline'
                        >
                            <Pencil className='h-4 w-4' />
                        </button>
                    </>
                )}
            </div>
        )
    }

    return (
        <div className='group/name flex min-w-0 items-start gap-1'>
            {editing ? (
                <input
                    ref={inputRef}
                    type='text'
                    value={draft}
                    disabled={saving}
                    onChange={(e) => setDraft(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault()
                            void saveEdit()
                        }
                        if (e.key === 'Escape') cancelEdit()
                    }}
                    onBlur={() => void saveEdit()}
                    className='w-full min-w-0 rounded border border-cz-purple-200 px-2 py-0.5 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-cz-focus/20'
                    aria-label='Baseline name'
                />
            ) : (
                <>
                    <button
                        type='button'
                        onClick={() => onOpen?.(run._id)}
                        className='min-w-0 flex-1 text-left'
                    >
                        <span className='font-semibold text-slate-900 group-hover/name:text-cz-purple-800'>
                            {displayName}
                        </span>
                        {showRecentHint ? (
                            <span className='mt-0.5 block text-[11px] font-medium text-cz-purple-700'>
                                Last opened
                                {recentStepLabel ? ` · ${recentStepLabel}` : ''}
                            </span>
                        ) : null}
                    </button>
                    <button
                        type='button'
                        onClick={startEdit}
                        className='mt-0.5 shrink-0 rounded p-1 text-slate-400 opacity-0 transition-opacity hover:bg-slate-100 hover:text-cz-purple-700 group-hover/name:opacity-100'
                        title='Rename baseline'
                        aria-label='Rename baseline'
                    >
                        <Pencil className='h-3.5 w-3.5' />
                    </button>
                </>
            )}
        </div>
    )
}
